import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash, CheckCircle, User, PencilSimple, Info, Link, Trophy, Target as TargetIcon, X, DownloadSimple } from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import ProjectBadge from "@/components/ProjectBadge";
import ProjectField, { assertProject, defaultProject } from "@/components/ProjectField";
import { useLang } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import PhotoUploader from "@/components/PhotoUploader";
import UserSelect from "@/components/UserSelect";

const observationPhotos = (row) => {
  const list = [...(row?.photos || [])];
  if (row?.photo && !list.includes(row.photo)) list.unshift(row.photo);
  return list;
};

const emptyForm = () => ({
  kind: "unsafe_condition", area: "", description: "", project_id: defaultProject(), project_id: defaultProject(),
  photos: [], immediate_action: "", severity: "medium", status: "open",
});

export default function ObservationsModule() {
  const { t } = useLang();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState(null);
  const [resolveOpen, setResolveOpen] = useState(null);
  const [resNote, setResNote] = useState("");
  const [resPhotos, setResPhotos] = useState([]);
  const [resolving, setResolving] = useState(false);
  const [filters, setFilters] = useState({ kind: "all", severity: "all", status: "open", responsible: "" });
  const canResolve = ["admin", "manager", "supervisor"].includes(user?.role);
  const canEdit = canResolve;

  const KIND_STYLE = {
    unsafe_act: { label: t("obsKindUnsafeAct"), cls: "bg-amber-100 text-amber-800 border-amber-200" },
    unsafe_condition: { label: t("obsKindUnsafeCondition"), cls: "bg-orange-100 text-orange-800 border-orange-200" },
    positive: { label: t("obsKindPositive"), cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  };
  const SEV_STYLE = {
    low: "bg-emerald-50 text-emerald-700",
    medium: "bg-amber-50 text-amber-700",
    high: "bg-red-100 text-red-700",
  };
  const SEV_LABEL = { low: t("obsSevLow"), medium: t("obsSevMedium"), high: t("obsSevHigh") };

  const load = useCallback(() => {
    api.get("/hse/observations").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const responsibles = useMemo(() => {
    const set = new Set();
    items.forEach((i) => { if (i.responsible) set.add(i.responsible); });
    return Array.from(set).sort();
  }, [items]);

  const filteredItems = useMemo(() => items.filter((it) => {
    if (filters.kind !== "all" && it.kind !== filters.kind) return false;
    if (filters.severity !== "all" && it.severity !== filters.severity) return false;
    if (filters.status !== "all") {
      if (filters.status === "open" && it.status === "closed") return false;
      if (filters.status === "closed" && it.status !== "closed") return false;
    }
    if (filters.responsible && (it.responsible || "").toLowerCase() !== filters.responsible.toLowerCase()) return false;
    return true;
  }), [items, filters]);

  const closeDialog = () => {
    setOpen(false);
    setEditingId(null);
    setForm(emptyForm());
  };

  const save = async () => {
    if (!assertProject(form.project_id)) return;
    try {
      if (editingId) {
        await api.put(`/hse/observations/${editingId}`, form);
        toast.success(t("obsUpdatedToast"));
      } else {
        await api.post("/hse/observations", form);
        toast.success(t("obsSavedToast"));
      }
      closeDialog();
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const startEdit = (it) => {
    setEditingId(it.id);
    setForm({
      kind: it.kind || "unsafe_condition",
      area: it.area || "",
      description: it.description || "",
      immediate_action: it.immediate_action || "",
      severity: it.severity || "medium",
      status: it.status || "open",
      photos: observationPhotos(it),
      responsible: it.responsible || "",
    });
    setOpen(true);
  };

  const remove = async (id) => {
    if (!window.confirm(t("obsDeleteConfirm"))) return;
    try { await api.delete(`/hse/observations/${id}`); load(); } catch (e) { toast.error(apiError(e)); }
  };

  const resolve = async (id) => {
    if (!resPhotos.length) {
      toast.error("Se requiere al menos una foto de evidencia para cerrar la OCAS");
      return;
    }
    setResolving(true);
    try {
      await api.post(`/hse/observations/${id}/resolve`, {
        note: resNote.trim(),
        photos: resPhotos,
      });
      toast.success(t("obsClosedToast"));
      setResolveOpen(null); setResNote(""); setResPhotos([]);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setResolving(false); }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-blue-100 bg-blue-50/60 p-2.5 flex items-start gap-2" data-testid="ocas-legend">
        <Info size={16} weight="fill" className="text-blue-700 mt-0.5 shrink-0" />
        <p className="text-[11px] text-blue-900 leading-tight">
          <b>{t("obsOcasFullName")}</b>
          <span className="text-slate-600"> — cada OCAS registra actos o condiciones fuera del estándar; al asignar responsable se crea automáticamente una acción correctiva vinculada.</span>
        </p>
      </div>
      <OcasRankingBar />
      <OcasFilters
        filters={filters}
        onChange={setFilters}
        responsibles={responsibles}
        stats={{
          total: items.length,
          open: items.filter((x) => x.status !== "closed").length,
          filtered: filteredItems.length,
        }}
      />
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <p className="text-sm text-slate-600">
            {items.length} {t("obsCountSuffix")} · {items.filter((x) => x.status !== "closed").length} {t("obsOpenSuffix")}
          </p>
        </div>
        <Dialog open={open} onOpenChange={(o) => { if (!o) closeDialog(); else setOpen(true); }}>
          <DialogTrigger asChild>
            <Button
              data-testid="obs-new-button"
              onClick={() => { setEditingId(null); setForm(emptyForm()); }}
              className="bg-red-600 hover:bg-red-700 gap-2"
            >
              <Plus size={14} weight="bold" /> {t("obsReportBtn")}
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">
                {editingId ? t("obsEditTitle") : t("obsNewTitle")}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <ProjectField value={form.project_id} onChange={(v) => setForm({ ...form, project_id: v })} testid="obs-project" />
              <div><Label className="overline-label">{t("obsFieldType")}</Label>
                <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                  <SelectTrigger data-testid="obs-kind"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="unsafe_act">{t("obsKindUnsafeAct")}</SelectItem>
                    <SelectItem value="unsafe_condition">{t("obsKindUnsafeCondition")}</SelectItem>
                    <SelectItem value="positive">{t("obsKindPositive")}</SelectItem>
                  </SelectContent>
                </Select></div>
              <div><Label className="overline-label">{t("obsFieldArea")}</Label>
                <Input data-testid="obs-area" value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} placeholder={t("obsPlaceholderArea")} /></div>
              <div><Label className="overline-label">{t("obsFieldDesc")}</Label>
                <Textarea data-testid="obs-description" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder={t("obsPlaceholderDesc")} /></div>
              <div><Label className="overline-label">{t("obsFieldAction")}</Label>
                <Textarea data-testid="obs-immediate-action" rows={2} value={form.immediate_action} onChange={(e) => setForm({ ...form, immediate_action: e.target.value })} placeholder={t("obsPlaceholderAction")} /></div>
              <div><Label className="overline-label">{t("obsFieldSeverity")}</Label>
                <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
                  <SelectTrigger data-testid="obs-severity"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="low">{t("obsSevLow")}</SelectItem>
                    <SelectItem value="medium">{t("obsSevMedium")}</SelectItem>
                    <SelectItem value="high">{t("obsSevHigh")}</SelectItem>
                  </SelectContent>
                </Select></div>
              <div>
                <Label className="overline-label">{t("obsFieldPhotos")}</Label>
                <div className="mt-1.5">
                  <PhotoUploader
                    value={form.photos || []}
                    onChange={(photos) => setForm({ ...form, photos })}
                    max={6}
                    testidPrefix="obs-photo"
                  />
                </div>
              </div>
              <div>
                <Label className="overline-label">{t("obsFieldResponsible")}</Label>
                <UserSelect
                  testid="obs-responsible"
                  value={form.responsible}
                  onChange={(v) => setForm({ ...form, responsible: v })}
                  placeholder="—"
                />
                <p className="text-[10px] text-slate-500 mt-1 flex items-start gap-1">
                  <Info size={11} weight="bold" className="mt-0.5 shrink-0 text-blue-700" />
                  {t("obsResponsibleHint")}
                </p>
              </div>
            </div>
            <Button data-testid="obs-save" onClick={save} disabled={!form.description || !form.area} className="bg-blue-900 hover:bg-blue-800 w-full">
              {editingId ? t("obsUpdateSubmit") : t("obsSubmit")}
            </Button>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {filteredItems.length === 0 && <p className="text-sm text-slate-400 text-center py-8 col-span-2 bg-white border border-slate-200 rounded-lg">{t("obsNoObservations")}</p>}
        {filteredItems.map((it) => {
          const k = KIND_STYLE[it.kind] || KIND_STYLE.unsafe_condition;
          const photos = observationPhotos(it);
          return (
            <div key={it.id} data-testid={`obs-card-${it.id}`} className={`bg-white border rounded-lg overflow-hidden ${it.status === "closed" ? "opacity-70" : ""}`}>
              {photos.length > 0 && (
                <div className="relative">
                  <img alt="" src={photos[0]} className="w-full h-32 object-cover" />
                  {photos.length > 1 && (
                    <span className="absolute bottom-1 right-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-black/70 text-white">
                      +{photos.length - 1}
                    </span>
                  )}
                </div>
              )}
              <div className="p-3">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <ProjectBadge id={it.project_id} />
                  <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border ${k.cls}`}>{k.label}</span>
                  <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${SEV_STYLE[it.severity]}`}>{SEV_LABEL[it.severity] || it.severity}</span>
                  {it.status === "closed" && <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded bg-slate-100 text-slate-500 flex items-center gap-0.5"><CheckCircle size={10} weight="fill" /> {t("obsClosedBadge")}</span>}
                </div>
                <p className="font-heading font-bold text-slate-900 text-sm truncate">{it.area}</p>
                <p className="text-xs text-slate-700 mt-1 line-clamp-3">{it.description}</p>
                {it.immediate_action && (
                  <p className="text-[11px] text-slate-500 italic mt-1"><b>{t("obsActionShort")}:</b> {it.immediate_action}</p>
                )}
                {it.responsible && (
                  <p className="text-[11px] text-slate-700 mt-1 flex items-center gap-1">
                    <User size={11} weight="bold" className="text-blue-700" />
                    <b>{t("obsFieldResponsible")}:</b> {it.responsible}
                    {it.linked_action_id && (
                      <span className="ml-1 inline-flex items-center gap-0.5 text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 border border-blue-200" data-testid={`obs-linked-action-${it.id}`}>
                        <Link size={9} weight="bold" /> {t("obsLinkedActionBadge")}
                      </span>
                    )}
                  </p>
                )}
                <div className="flex items-center justify-between mt-2 text-[10px] text-slate-400">
                  <span className="flex items-center gap-1"><User size={10} /> {it.reported_by} · {(it.reported_at || "").slice(0, 10)}</span>
                  <div className="flex items-center gap-2">
                    {canEdit && it.status !== "closed" && (
                      <button data-testid={`obs-edit-${it.id}`} onClick={() => startEdit(it)} className="text-blue-700 hover:underline flex items-center gap-0.5">
                        <PencilSimple size={10} weight="bold" /> {t("obsEdit")}
                      </button>
                    )}
                    {canResolve && it.status !== "closed" && (
                      <button data-testid={`obs-resolve-${it.id}`} onClick={() => setResolveOpen(it.id)} className="text-emerald-700 hover:underline">{t("obsClose")}</button>
                    )}
                    {canResolve && (
                      <button data-testid={`obs-delete-${it.id}`} onClick={() => remove(it.id)} className="text-slate-400 hover:text-red-500"><Trash size={12} /></button>
                    )}
                  </div>
                </div>
                {it.shift_owner_name && (
                  <div data-testid={`obs-shift-owner-${it.id}`} className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-800 border border-emerald-200">
                    <User size={9} weight="bold" /> {t("shiftOwnerTag")} · {it.shift_owner_name}
                  </div>
                )}
                {it.resolution_note && <p className="text-[11px] text-emerald-700 mt-1"><b>{t("obsResolutionLabel")}:</b> {it.resolution_note}</p>}
              </div>
            </div>
          );
        })}
      </div>

      {resolveOpen && (
        <Dialog open onOpenChange={(o) => { if (!o) { setResolveOpen(null); setResNote(""); setResPhotos([]); } }}>
          <DialogContent className="bg-white max-w-lg max-h-[92vh] overflow-y-auto" data-testid="obs-resolve-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                <CheckCircle weight="bold" className="text-emerald-600" />
                {t("obsResolveTitle")}
              </DialogTitle>
            </DialogHeader>

            {/* Info auto-computada por el backend (audit trail) */}
            <div className="bg-slate-50 border border-slate-200 rounded p-3 grid grid-cols-2 gap-3 text-xs">
              <div>
                <div className="text-slate-500 uppercase tracking-wider text-[10px]">Responsable del cierre</div>
                <div className="font-semibold text-slate-800 flex items-center gap-1 mt-0.5">
                  <User size={12} weight="bold" /> {user?.name || user?.email || "-"}
                </div>
              </div>
              <div>
                <div className="text-slate-500 uppercase tracking-wider text-[10px]">Fecha de cierre</div>
                <div className="font-semibold text-slate-800 flex items-center gap-1 mt-0.5 font-mono">
                  {new Date().toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" })}
                </div>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded p-2 text-[11px] text-amber-900">
              📸 <b>Evidencia obligatoria:</b> sube al menos una foto de la corrección aplicada. Si la OCAS tiene una acción correctiva vinculada, la misma evidencia se copia automáticamente al cerrarla.
            </div>

            <div>
              <Label className="overline-label">Fotos de evidencia (máx 6) *</Label>
              <PhotoUploader
                value={resPhotos}
                onChange={setResPhotos}
                max={6}
                testidPrefix="obs-resolve-photos"
              />
              {resPhotos.length === 0 && (
                <p className="text-[11px] text-red-600 mt-1">⚠ Sin foto no se puede cerrar</p>
              )}
            </div>

            <div>
              <Label className="overline-label">{t("obsResolveNote")}</Label>
              <Textarea
                data-testid="obs-resolve-note"
                rows={3}
                placeholder="Describe qué se hizo para corregir. Ej: 'Se reubicó el extintor y se capacitó al supervisor de taller.'"
                value={resNote}
                onChange={(e) => setResNote(e.target.value)}
              />
            </div>

            <div className="flex gap-2 justify-end pt-2">
              <Button variant="ghost" onClick={() => { setResolveOpen(null); setResNote(""); setResPhotos([]); }} disabled={resolving}>
                Cancelar
              </Button>
              <Button
                data-testid="obs-resolve-confirm"
                onClick={() => resolve(resolveOpen)}
                disabled={resolving || resPhotos.length === 0}
                className="bg-emerald-600 hover:bg-emerald-700 gap-1"
              >
                <CheckCircle size={14} weight="bold" /> {resolving ? "Cerrando…" : t("obsResolveConfirm")}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// ---------- OCAS Filters ----------
function OcasFilters({ filters, onChange, responsibles, stats }) {
  const chip = (val, activeVal, label, key) => (
    <button
      key={`${key}-${val}`}
      data-testid={`ocas-filter-${key}-${val}`}
      onClick={() => onChange({ ...filters, [key]: val })}
      className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border transition ${activeVal === val ? "bg-blue-900 text-white border-blue-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}
    >{label}</button>
  );
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-3 space-y-2" data-testid="ocas-filters">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] uppercase tracking-widest font-bold text-slate-400">Tipo</span>
        {chip("all", filters.kind, "Todos", "kind")}
        {chip("unsafe_act", filters.kind, "Acto", "kind")}
        {chip("unsafe_condition", filters.kind, "Condición", "kind")}
        {chip("positive", filters.kind, "Positiva", "kind")}
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] uppercase tracking-widest font-bold text-slate-400">Severidad</span>
        {chip("all", filters.severity, "Todas", "severity")}
        {chip("low", filters.severity, "Baja", "severity")}
        {chip("medium", filters.severity, "Media", "severity")}
        {chip("high", filters.severity, "Alta", "severity")}
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] uppercase tracking-widest font-bold text-slate-400">Estado</span>
        {chip("all", filters.status, "Todas", "status")}
        {chip("open", filters.status, "Abiertas", "status")}
        {chip("closed", filters.status, "Cerradas", "status")}
      </div>
      <div className="flex items-center gap-2 flex-wrap pt-1 border-t border-slate-100">
        <span className="text-[10px] uppercase tracking-widest font-bold text-slate-400">Responsable</span>
        <select
          value={filters.responsible}
          onChange={(e) => onChange({ ...filters, responsible: e.target.value })}
          data-testid="ocas-filter-responsible"
          className="text-[11px] font-semibold px-2 py-1 rounded border border-slate-200 bg-white text-slate-700 max-w-[220px]"
        >
          <option value="">— cualquiera —</option>
          {responsibles.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        {filters.responsible && (
          <button
            data-testid="ocas-filter-responsible-clear"
            onClick={() => onChange({ ...filters, responsible: "" })}
            className="inline-flex items-center gap-0.5 text-[10px] text-slate-500 hover:text-red-600"
          ><X size={10} weight="bold" /> limpiar</button>
        )}
        <div className="flex-1" />
        <span className="text-[10px] text-slate-500">
          Mostrando <b className="text-slate-900" data-testid="ocas-filter-count">{stats.filtered}</b> de {stats.total} OCAS
          <span className="ml-2 text-amber-700">{stats.open} abiertas</span>
        </span>
      </div>
    </div>
  );
}

// ---------- OCAS Ranking (mini-podium) ----------
const MEDAL = ["🥇", "🥈", "🥉"];
const MEDAL_BG = ["bg-amber-100 border-amber-300", "bg-slate-100 border-slate-300", "bg-orange-100 border-orange-300"];

function OcasRankingBar() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/hse/observations/ranking?limit=5")
      .then((r) => setData(r.data))
      .catch(() => setData({ month: "", ranking: [], total_month: 0, target_month: 0, compliance_pct: null }));
  }, []);
  if (!data) return null;

  const complianceColor = data.compliance_pct == null
    ? "text-slate-600"
    : data.compliance_pct >= 100 ? "text-emerald-700"
    : data.compliance_pct >= 70 ? "text-amber-700" : "text-red-600";

  return (
    <div className="bg-gradient-to-br from-amber-50 via-white to-blue-50 border border-amber-200 rounded-lg p-3" data-testid="ocas-ranking">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
        <div className="flex items-center gap-2">
          <Trophy size={18} weight="fill" className="text-amber-500" />
          <p className="text-xs uppercase tracking-widest font-bold text-slate-700">
            Ranking de reporteros · {data.month || "—"}
          </p>
        </div>
        <div className="flex items-center gap-2 text-[11px]">
          <TargetIcon size={12} weight="fill" className="text-blue-800" />
          <span className="text-slate-600">
            <span data-testid="ocas-ranking-total">{data.total_month}</span>
            <span className="text-slate-400"> / {data.target_month || "—"} meta ·</span>
          </span>
          <span className={`font-bold ${complianceColor}`} data-testid="ocas-ranking-compliance">
            {data.compliance_pct != null ? `${data.compliance_pct}%` : "sin meta"}
          </span>
          <button
            onClick={async () => {
              try {
                const r = await api.get(`/hse/observations/ranking/pdf?month=${data.month || ""}`, { responseType: "blob" });
                const url = URL.createObjectURL(r.data);
                const a = document.createElement("a");
                a.href = url; a.download = `podio-ocas-${data.month}.pdf`; a.click();
                URL.revokeObjectURL(url);
              } catch (e) { toast.error(apiError(e)); }
            }}
            data-testid="ocas-ranking-export"
            className="ml-1 inline-flex items-center gap-0.5 text-[10px] font-bold uppercase px-2 py-1 rounded bg-amber-500 hover:bg-amber-400 text-slate-900 border border-amber-600"
            title="Descargar podio como PDF para pegar en el tablero"
          >
            <DownloadSimple size={11} weight="bold" /> Podio PDF
          </button>
          <button
            onClick={async () => {
              try {
                const r = await api.get(`/hse/observations/audit-monthly/pdf?month=${data.month || ""}`, { responseType: "blob" });
                const url = URL.createObjectURL(r.data);
                const a = document.createElement("a");
                a.href = url; a.download = `auditoria-ocas-${data.month}.pdf`; a.click();
                URL.revokeObjectURL(url);
              } catch (e) { toast.error(apiError(e)); }
            }}
            data-testid="ocas-audit-export"
            className="inline-flex items-center gap-0.5 text-[10px] font-bold uppercase px-2 py-1 rounded bg-emerald-600 hover:bg-emerald-500 text-white border border-emerald-700"
            title="Descargar auditoría mensual con TODAS las OCAS y evidencia fotográfica ANTES (reporte) y DESPUÉS (cierre)"
          >
            <DownloadSimple size={11} weight="bold" /> OCAS c/ antes y después
          </button>
        </div>
      </div>
      {data.ranking.length === 0 ? (
        <p className="text-[11px] text-slate-400 italic text-center py-2">Aún no hay OCAS reportadas este mes — ¡sé la primera persona!</p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          {data.ranking.map((r, i) => (
            <div
              key={r.name}
              data-testid={`ocas-ranking-row-${i}`}
              className={`border rounded-md p-2 text-center ${i < 3 ? MEDAL_BG[i] : "bg-white border-slate-200"}`}
            >
              <p className="text-lg leading-none">{MEDAL[i] || `#${i + 1}`}</p>
              <p className="font-bold text-[11px] text-slate-800 truncate mt-1" title={r.name}>{r.name}</p>
              <p className="font-heading font-black text-2xl leading-tight text-blue-900">{r.total}</p>
              <p className="text-[9px] text-slate-500 leading-tight">
                {r.high > 0 && <span className="text-red-600 font-bold">{r.high} alta</span>}
                {r.high > 0 && (r.acts + r.conditions + r.positive) > 0 && " · "}
                {r.acts > 0 && `${r.acts} acto`}
                {r.acts > 0 && r.conditions > 0 && " · "}
                {r.conditions > 0 && `${r.conditions} cond`}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
